import React from 'react'
import styled, {css} from 'styled-components'
import Box from '../layout/Box'

export interface GridProps {
  align?: 'top' | 'center' | 'bottom' | 'stretch'
  columns?: number
  isRtl?: boolean
  columnsSizes?: number[]
}

const normalizeAlign = (align: 'top' | 'center' | 'bottom' | 'stretch') => {
  if (align === 'top') {
    return 'flex-start'
  }

  if (align === 'bottom') {
    return 'flex-end'
  }

  return align
}

export const GridColumn = styled(Box)<{size?: number; columns?: number}>`
  ${({theme, size = 1, columns = 1}) => {
    let columnWidth

    if (columns) {
      columnWidth = ((100 / columns) * size).toFixed(2)
    }

    return css`
      ${theme.mq.small({
        maxWidth: '100%',
        flex: '0 0 100%',
      })}

      ${columnWidth &&
      css`
        max-width: ${columnWidth}%;
        flex: 0 0 ${columnWidth}%;
      `}
    `
  }}
`

const GridBase: React.FC<GridProps> = ({
  children,
  align = 'center',
  columns,
  // define possibility to have bigger columns
  columnsSizes = [],
  // avoid passing `isRtl` to HTML elements
  isRtl: _isRtl,
  ...restProps
}) => {
  return (
    <Box
      $mx={[null, -10, -10, -10]}
      $styles={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: normalizeAlign(align),
      }}
      {...restProps}
    >
      {React.Children.map(children, (child, index) => (
        <GridColumn
          columns={columns}
          size={columnsSizes[index] || 1}
          $px={[null, 10, 10, 10]}
          $styles={{
            maxWidth: ['100%', null, null, null],
            flex: ['0 0 100%', null, null, null],
          }}
        >
          {child}
        </GridColumn>
      ))}
    </Box>
  )
}

const Grid = styled(GridBase)`
  ${({isRtl}) =>
    isRtl &&
    css`
      ${GridColumn}:first-child {
        order: 2;
        padding-left: 8.33%;
        position: relative;

        ${({theme}) =>
          theme.normalizeStyles({
            left: [null, 10, 10, 10],
            order: [1, 2, 2, 2],
            paddingLeft: [0, '8.33%', '8.33%', '8.33%'],
          })}
      }

      ${GridColumn}:last-child {
        ${({theme}) =>
          theme.normalizeStyles({
            order: [2, 1, 1, 1],
          })}
      }
    `}
`

export default Grid
